/**
 * Чистая JS-реализация SHA-256 для сред без secure context
 * (HTTP по IP в локальной сети, где недоступен window.crypto.subtle).
 *
 * Не зависит от сторонних библиотек. Результат побайтно совпадает с нативным
 * Web Crypto и Node crypto (проверено на тест-векторах NIST и PIN '1234').
 */
function sha256Fallback(ascii: string): string {
  function rightRotate(value: number, amount: number): number {
    return (value >>> amount) | (value << (32 - amount));
  }

  let result = '';
  const words: number[] = [];
  const asciiBitLength = ascii.length * 8;

  const hash = [
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a,
    0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
  ];
  const k = [
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
  ];

  let i: number;
  let j: number;
  let activeHash: number[];

  for (i = 0; i < ascii.length; i++) {
    const jIdx = i >> 2;
    words[jIdx] = (words[jIdx] || 0) | (ascii.charCodeAt(i) << ((3 - (i % 4)) * 8));
  }

  const wordCount = asciiBitLength >> 5;
  words[wordCount] = (words[wordCount] || 0) | (0x80 << ((3 - (ascii.length % 4)) * 8));
  words[(((asciiBitLength + 64) >> 9) << 4) + 15] = asciiBitLength;

  for (let chunk = 0; chunk < words.length; chunk += 16) {
    const w: number[] = [];
    activeHash = hash.slice(0);

    for (i = 0; i < 64; i++) {
      if (i < 16) {
        w[i] = words[chunk + i] || 0;
      } else {
        const s0 =
          rightRotate(w[i - 15], 7) ^ rightRotate(w[i - 15], 18) ^ (w[i - 15] >>> 3);
        const s1 =
          rightRotate(w[i - 2], 17) ^ rightRotate(w[i - 2], 19) ^ (w[i - 2] >>> 10);
        w[i] = (((w[i - 16] + s0) | 0) + ((w[i - 7] + s1) | 0)) | 0;
      }

      const S1 =
        rightRotate(activeHash[4], 6) ^
        rightRotate(activeHash[4], 11) ^
        rightRotate(activeHash[4], 25);
      const ch = (activeHash[4] & activeHash[5]) ^ (~activeHash[4] & activeHash[6]);
      // Важно: w[i] входит в сумму temp1 (явная скобка — иначе приоритет
      // операторов «|» / «+» теряет слагаемое и ломает хэш).
      const temp1 = (((((activeHash[7] + S1) | 0) + ((ch + k[i]) | 0)) | 0) + w[i]) | 0;

      const S0 =
        rightRotate(activeHash[0], 2) ^
        rightRotate(activeHash[0], 13) ^
        rightRotate(activeHash[0], 22);
      const maj =
        (activeHash[0] & activeHash[1]) ^
        (activeHash[0] & activeHash[2]) ^
        (activeHash[1] & activeHash[2]);
      const temp2 = (S0 + maj) | 0;

      activeHash[7] = activeHash[6];
      activeHash[6] = activeHash[5];
      activeHash[5] = activeHash[4];
      activeHash[4] = (activeHash[3] + temp1) | 0;
      activeHash[3] = activeHash[2];
      activeHash[2] = activeHash[1];
      activeHash[1] = activeHash[0];
      activeHash[0] = (temp1 + temp2) | 0;
    }

    for (j = 0; j < 8; j++) {
      hash[j] = (hash[j] + activeHash[j]) | 0;
    }
  }

  for (i = 0; i < 8; i++) {
    for (j = 3; j >= 0; j--) {
      const b = (hash[i] >> (j * 8)) & 255;
      result += (b < 16 ? '0' : '') + b.toString(16);
    }
  }

  return result;
}

/**
 * Хэширует строку в SHA-256.
 *
 * В secure-контексте (HTTPS/localhost) использует нативный Web Crypto API.
 * В обычных HTTP LAN-сетях (http по IP, где window.crypto.subtle недоступен)
 * прозрачно переключается на чистый JS-фолбэк без сторонних зависимостей.
 */
export async function sha256Hex(message: string): Promise<string> {
  if (typeof window !== 'undefined' && window.crypto?.subtle?.digest) {
    try {
      const msgUint8 = new TextEncoder().encode(message);
      const hashBuffer = await window.crypto.subtle.digest('SHA-256', msgUint8);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
    } catch {
      // Фолбэк при любой непредвиденной ошибке CryptoSubtle.
    }
  }
  return sha256Fallback(message);
}

/**
 * Фолбэк-генератор UUIDv4 без Web Crypto.
 *
 * Нужен в non-secure context (HTTP по IP, старые WebView, где нет
 * `crypto.randomUUID`) — касса в локальной сети работает именно так.
 */
export function uuidFallback(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Генерирует валидный RFC4122 UUIDv4.
 * Безопасен для Non-Secure Context (HTTP по локальному IP), где
 * crypto.randomUUID может быть недоступен.
 */
export function generateUUID(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    try {
      return crypto.randomUUID();
    } catch {
      // Фолбэк ниже.
    }
  }
  return uuidFallback();
}

/** Проверяет, является ли строка валидным UUID (версии 1-5). */
export function isValidUUID(str: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(str);
}
